import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Grid, MenuItem } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { searchPayload } from "../../../utils/common";
import CustomTextField from "../../../components/CustomTextField";
import ReactHookFormSelect from "../../../components/ReactHookFormSelect";
import SearchableAutoComplete from "../../../components/SearchableAutoComplete";
import useUser from "../../../hooks/useUser";
import useComplaintType from "../../../hooks/useComplaintType";
import useEmployee from "../../../hooks/useEmployee";
import useTenant from "../../../hooks/useTenant";
import useAuth from "../../../hooks/useAuth";
import CustomCheckBox from "../../../components/CustomCheckBox";
import CustomFileField from "../../../components/CustomFileField";
import usePlanGroup from "../../../hooks/usePlanGroup";
import usePincode from "../../../hooks/usePincode";
import axios from "axios";
import CustomDatePicker from "../../../components/CustomDatePicker";

const currentPlanGroupPlanPayload = {
  filters: [],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [],
};
const currentTenantPayload = {
  filters: [
    {
      fieldName: "businessType",
      fieldValue: "LOCAL_CABLE_OPERATOR",
      op: "eq",
      dataType: "businessType",
      isJoin: false,
      condition: "OR",
    },
    {
      fieldName: "businessType",
      fieldValue: "BULK_BANDWIDTH",
      op: "eq",
      dataType: "businessType",
      isJoin: false,
      condition: "OR",
    },
  ],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [],
};

function CafFields(props) {
  const { autoSearchTenant } = useTenant();
  const { validationFields, presentAddressFields, isEdit } = props;

  const [planGroupPlanPayload, setPlanGroupPlanPayload] = useState(currentPlanGroupPlanPayload);

  const [planGroupPlanOptions, setPlanGroupPlanOptions] = useState([]);

  const { autoSearchPlanGroupPlan, planGroupPlanAutoSearchResults } = usePlanGroup();

  const { t } = useTranslation();
  const {
    formState: { errors },
    control,
    watch,
    setValue,
  } = useFormContext();

  const ipRequired = watch("ipRequired");

  const tenant = watch("tenant");

  const fetchPlanGroupPlans = async () => {
    if (Object.keys(planGroupPlanPayload).length != 0 && planGroupPlanPayload.filters.length != 0) {
      await autoSearchPlanGroupPlan(planGroupPlanPayload);
    }
  };

  useEffect(() => {
    fetchPlanGroupPlans();
  }, [planGroupPlanPayload]);

  useEffect(() => {
    if (tenant && Object.keys(tenant).length != 0) {
      setPlanGroupPlanOptions([]);
      setPlanGroupPlanPayload({
        ...planGroupPlanPayload,
        filters: [
          {
            fieldName: "tenantId",
            fieldValue: tenant.value,
            op: "eq",
            dataType: "int",
            condition: "and",
            isJoin: true,
          },
        ],
      });
    } else {
      setPlanGroupPlanOptions([]);
    }
  }, [tenant]);

  const [tenantAutoSearchResults, setTenantAutoSearchResults] = useState({
    data: [],
  });

  const [tenantPayload, setTenantPayload] = useState(currentTenantPayload);
  const [tenantOptions, setTenantOptions] = useState([]);

  useEffect(() => {
    if (tenantAutoSearchResults && tenantAutoSearchResults.data.length !== 0) {
      const results = [...tenantOptions, ...tenantAutoSearchResults.data];
      const filteredResults = results.filter(
        (value, index, self) => index === self.findIndex((t) => t.value === value.value),
      );
      filteredResults.sort((a, b) => a.label.localeCompare(b.label));
      setTenantOptions(filteredResults);
    }
  }, [tenantAutoSearchResults]);

  const fetchTenants = async () => {
    const tenantResults = await autoSearchTenant(tenantPayload);
    setTenantAutoSearchResults(tenantResults);
  };

  useEffect(() => {
    fetchTenants();
  }, [tenantPayload]);

  useEffect(() => {
    //console.log(ispOptions, "ispOptions", ispAutoSearchResults);
    if (planGroupPlanAutoSearchResults.data.length !== 0) {
      const results = [...planGroupPlanOptions, ...planGroupPlanAutoSearchResults.data];
      const filteredResults = results.filter(
        (value, index, self) => index === self.findIndex((t) => t.value === value.value),
      );
      filteredResults.sort((a, b) => a.label.localeCompare(b.label));
      setPlanGroupPlanOptions(filteredResults);
    }
  }, [planGroupPlanAutoSearchResults]);

  return (
    <Grid container spacing={12} className="row" pt={8}>
      <Grid item xs={8.5} sm={4.5} md={6} lg={6} xl={6}>
        <SearchableAutoComplete
          control={control}
          data={tenantOptions}
          label={t("select_franchise")}
          payload={tenantPayload}
          setPayload={setTenantPayload}
          loading={false}
          name="tenant"
          multiple={false}
          defaultValue={""}
          hasNextPage={false}
          fields={validationFields}
          errors={errors}
        />
      </Grid>

      <Grid item md={6}>
        <CustomTextField
          label={t("full_name")}
          name="fullName"
          control={control}
          fullWidth
          variant="outlined"
          errors={errors}
          fields={validationFields}
        />
      </Grid>
      <Grid item md={6}>
        <CustomTextField
          label={t("user_name")}
          name="userName"
          control={control}
          fullWidth
          variant="outlined"
          errors={errors}
          fields={validationFields}
        />
      </Grid>
      <Grid item md={6}>
        <CustomTextField
          label={t("email_id")}
          name="emailId"
          control={control}
          fullWidth
          variant="outlined"
          errors={errors}
          fields={validationFields}
        />
      </Grid>
      <Grid item md={6}>
        <CustomTextField
          label={t("mobile_number")}
          name="mobileNo"
          control={control}
          fullWidth
          variant="outlined"
          errors={errors}
          fields={validationFields}
        />
      </Grid>
      <Grid item xs={8.5} sm={4.5} md={6} lg={6} xl={6}>
        <SearchableAutoComplete
          control={control}
          data={planGroupPlanOptions}
          label="Select Plan"
          payload={planGroupPlanPayload}
          setPayload={setPlanGroupPlanPayload}
          loading={false}
          name="plan"
          multiple={false}
          defaultValue={""}
          hasNextPage={false}
          fields={validationFields}
        />
      </Grid>

      <Grid item xs={8.5} sm={4.5} md={6} lg={6} xl={6}>
        <ReactHookFormSelect
          name="connectionType"
          label={t("connection_type")}
          control={control}
          fullWidth
          variant="outlined"
          errors={errors}
          fields={validationFields}
        >
          <MenuItem value={"HOME_USER"}>Home User</MenuItem>
          <MenuItem value={"BUSINESS_USER"}>Business User</MenuItem>
        </ReactHookFormSelect>
      </Grid>

      <Grid item xs={8.5} sm={4.5} md={6} lg={6} xl={6}>
        <ReactHookFormSelect
          name="customerMileType"
          label={t("customer_mile_type")}
          control={control}
          fullWidth
          variant="outlined"
          errors={errors}
          fields={validationFields}
        >
          <MenuItem value={"FIBER"}>Fiber</MenuItem>
          <MenuItem value={"RF_TOWER"}>RF Tower</MenuItem>
        </ReactHookFormSelect>
      </Grid>
      <Grid item md={6}>
        <CustomTextField
          label={t("router_no")}
          name="routerNo"
          control={control}
          fullWidth
          variant="outlined"
          errors={errors}
          fields={validationFields}
        />
      </Grid>

      <Grid item md={6}>
        <CustomDatePicker
          name="activationDate"
          nameTemp="activationDateStr"
          label={t("activation_date")}
          range={false}
          control={control}
          fields={validationFields}
          timePicker={false}
          format="DD-MM-YYYY"
          conversionFormat="YYYY-MM-DD"
        />
      </Grid>
      <Grid item xs={9.5} sm={12} md={6} lg={6} xl={4}>
        <CustomCheckBox
          label={t("static_ip_required")}
          name="ipRequired"
          type="checkbox"
          my={2}
          fullWidth
          errors={errors}
          fields={validationFields}
        />
      </Grid>
      {ipRequired && (
        <Grid item md={6}>
          <CustomTextField
            label={t("ip_address")}
            name="ipAddress"
            control={control}
            fullWidth
            variant="outlined"
            errors={errors}
            fields={validationFields}
          />
        </Grid>
      )}
    </Grid>
  );
}

export default CafFields;
